// Corporate Governance Review Methodology

export const methodologyData = {
  title: "Corporate Governance Review Methodology",
  subtitle: "A structured approach to conducting a comprehensive internal governance review",
  
  overview: {
    definition: "A corporate governance review is a systematic, evidence-based assessment of an organisation's governance framework, structures, processes, and culture. It evaluates the effectiveness of the board, the quality of oversight, regulatory compliance, and the organisation's capacity to deliver its strategic objectives through sound governance practices.",
    
    objectives: [
      "Assess the adequacy and effectiveness of governance structures and processes",
      "Evaluate board composition, capability, and performance",
      "Identify governance risks and control weaknesses",
      "Ensure compliance with legal, regulatory, and best practice standards",
      "Assess the quality of strategic oversight and decision-making",
      "Evaluate information flow and management reporting",
      "Assess governance culture and behavioral indicators",
      "Provide evidence-based recommendations for improvement"
    ],
    
    scope: "This methodology covers all aspects of corporate governance including: board structure and effectiveness, strategic oversight, risk governance, financial controls, regulatory compliance, stakeholder accountability, information governance, and governance culture. The review encompasses both structural elements (what exists) and effectiveness elements (how well it functions).",
    
    expectedOutcomes: [
      "Comprehensive governance assessment report",
      "RAG-rated governance scorecard",
      "Prioritised risk register",
      "Evidence-based recommendations",
      "90-day action plan",
      "12-month governance improvement roadmap"
    ]
  },

  fourPhaseProcess: {
    description: "This methodology follows a structured four-phase approach, mirroring professional consultancy practice. Each phase builds upon the previous, moving from planning through to implementation.",
    
    phases: [
      {
        phase: 1,
        name: "Planning & Scoping",
        duration: "1-2 weeks",
        description: "Establish the review's objectives, scope, and methodology. Define what will be reviewed, who will be involved, and how evidence will be gathered.",
        
        keyActivities: [
          "Define review objectives and success criteria",
          "Establish scope boundaries (what's in/out)",
          "Identify key stakeholders and governance actors",
          "Determine document requirements",
          "Plan interview schedule",
          "Set timeline and milestones",
          "Establish evaluation criteria and scoring framework",
          "Create evidence tracking system"
        ],
        
        outputs: [
          "Review scope statement",
          "Stakeholder map",
          "Document request list",
          "Interview schedule",
          "Project timeline"
        ],
        
        criticalSuccessFactors: [
          "Clear objectives aligned to organisational context",
          "Realistic scope that can be executed with available resources",
          "Board buy-in and active participation",
          "Access to all necessary documents and personnel",
          "Defined evaluation criteria agreed upfront"
        ]
      },
      
      {
        phase: 2,
        name: "Documentation & Structural Review",
        duration: "2-3 weeks",
        description: "Systematic review of all governance documentation to assess structural adequacy, regulatory compliance, and documentation quality. This phase examines what exists on paper.",
        
        keyActivities: [
          "Review governing document (Articles, Constitution)",
          "Assess board and committee terms of reference",
          "Evaluate scheme of delegation and reserved matters",
          "Analyze board minutes for oversight evidence",
          "Review risk register adequacy",
          "Assess policy framework completeness",
          "Evaluate conflicts of interest management",
          "Review governance calendar and board pack structure",
          "Check regulatory filing compliance",
          "Document findings systematically using RAG ratings"
        ],
        
        outputs: [
          "Document review assessment for each category",
          "RAG ratings by document type",
          "Gap analysis",
          "Compliance status summary",
          "Structural risk identification"
        ],
        
        evaluationCriteria: [
          "Regulatory compliance (Charity Commission, Companies House, sector-specific)",
          "Alignment to Charity Governance Code",
          "Clarity and comprehensiveness",
          "Currency (last review date)",
          "Internal consistency",
          "Fit-for-purpose assessment"
        ]
      },
      
      {
        phase: 3,
        name: "Effectiveness, Risk & Culture Assessment",
        duration: "2-3 weeks",
        description: "Move beyond documentation to assess actual governance effectiveness through structured interviews, behavioral observation, and culture indicators. This phase examines how governance functions in practice.",
        
        keyActivities: [
          "Conduct structured interviews with Chair, CEO, Trustees",
          "Assess board effectiveness and dynamics",
          "Evaluate decision-making quality",
          "Assess information flow and reporting quality",
          "Identify cultural strengths and weaknesses",
          "Detect governance red flags",
          "Evaluate risk management effectiveness",
          "Assess strategic oversight quality",
          "Synthesize findings across all evidence sources"
        ],
        
        outputs: [
          "Interview summaries by stakeholder type",
          "Effectiveness assessment by governance dimension",
          "Cultural indicators (strengths and concerns)",
          "Red flag register",
          "Governance effectiveness rating"
        ],
        
        diagnosticAreas: [
          "Board composition and capability",
          "Role clarity (Chair, CEO, Trustees)",
          "Strategic oversight quality",
          "Financial oversight effectiveness",
          "Risk governance maturity",
          "Decision-making processes",
          "Information quality and flow",
          "Governance culture and behaviors",
          "Committee effectiveness",
          "Succession planning"
        ]
      },
      
      {
        phase: 4,
        name: "Findings, Risk Ranking & Improvement Planning",
        duration: "1-2 weeks",
        description: "Synthesize all findings into a comprehensive governance review report, prioritize risks, and develop actionable improvement recommendations with implementation timelines.",
        
        keyActivities: [
          "Calculate overall governance rating",
          "Classify and prioritize governance risks",
          "Formulate evidence-based recommendations",
          "Develop 90-day action plan",
          "Create 12-month governance improvement roadmap",
          "Draft professional review report",
          "Prepare board presentation materials"
        ],
        
        outputs: [
          "Corporate Governance Review Report",
          "Governance scorecard (RAG-rated)",
          "Risk classification matrix",
          "Priority recommendations",
          "90-day action plan",
          "12-month improvement roadmap",
          "Board presentation deck"
        ],
        
        reportStructure: [
          "Executive Summary",
          "Review Scope & Methodology",
          "Overall Governance Rating",
          "Key Strengths",
          "Key Risks & Concerns",
          "Detailed Findings by Category",
          "RAG Summary Table",
          "Risk Classification & Prioritization",
          "Recommendations",
          "Implementation Planning"
        ]
      }
    ]
  },

  evidenceGatheringApproach: {
    description: "Evidence must be gathered from multiple sources to form a triangulated, balanced view of governance quality.",
    
    sources: [
      {
        type: "Documentary Evidence",
        examples: [
          "Governing document",
          "Board and committee terms of reference",
          "Board minutes (last 12-18 months)",
          "Strategic plan",
          "Risk register",
          "Financial reports and management accounts",
          "Policies and procedures",
          "Annual report and accounts",
          "Conflicts of interest register",
          "Board papers and information packs"
        ]
      },
      {
        type: "Interview Evidence",
        stakeholders: [
          "Chair",
          "CEO / Executive Director",
          "Trustees (sample or all)",
          "Committee Chairs",
          "Senior management (optional)"
        ]
      },
      {
        type: "Observational Evidence",
        indicators: [
          "Quality of board papers",
          "Minute quality and evidence of challenge",
          "Information flow timeliness",
          "Documentation currency",
          "Professional tone and standards"
        ]
      }
    ]
  },

  scoringFramework: {
    description: "A structured scoring methodology using RAG ratings and weighted scores across governance dimensions.",
    
    ragDefinitions: {
      red: {
        label: "Red - Significant Concern",
        definition: "Major governance weakness, regulatory risk, or control failure. Immediate action required.",
        scoring: "0-40% compliance or effectiveness"
      },
      amber: {
        label: "Amber - Requires Improvement",
        definition: "Governance gaps, inconsistencies, or areas not meeting best practice. Action required within defined timeframe.",
        scoring: "41-74% compliance or effectiveness"
      },
      green: {
        label: "Green - Satisfactory",
        definition: "Governance standards met, controls effective, best practice evident. Minor enhancements only.",
        scoring: "75-100% compliance or effectiveness"
      }
    },
    
    scoringDimensions: [
      "Governance Structure & Documentation",
      "Board Composition & Capability",
      "Strategic Oversight",
      "Financial Governance",
      "Risk Governance",
      "Regulatory Compliance",
      "Decision-Making Effectiveness",
      "Information Governance",
      "Governance Culture",
      "Stakeholder Accountability"
    ],
    
    overallRatingBands: [
      { range: "0-40%", rating: "Inadequate Governance", color: "red", action: "Urgent remedial action required" },
      { range: "41-60%", rating: "Developing Governance", color: "amber", action: "Significant improvements needed" },
      { range: "61-74%", rating: "Adequate Governance", color: "amber", action: "Enhancements required" },
      { range: "75-89%", rating: "Good Governance", color: "green", action: "Minor improvements only" },
      { range: "90-100%", rating: "Excellent Governance", color: "green", action: "Maintain and refine" }
    ]
  },

  riskClassification: {
    description: "Governance risks must be classified by type, likelihood, and impact to enable prioritisation.",
    
    riskTypes: [
      {
        type: "Regulatory Risk",
        definition: "Risk of non-compliance with legal or regulatory requirements",
        examples: ["Charity Commission reporting failure", "Companies House filing breach", "Data protection non-compliance"]
      },
      {
        type: "Reputational Risk",
        definition: "Risk of damage to organisational standing or public trust",
        examples: ["Poor governance practices becoming public", "Trustee conflicts of interest", "Financial mismanagement"]
      },
      {
        type: "Operational Risk",
        definition: "Risk of governance failures affecting operational delivery",
        examples: ["Inadequate delegation causing bottlenecks", "Poor information flow affecting decisions", "Weak committee oversight"]
      },
      {
        type: "Financial Risk",
        definition: "Risk of financial loss or control failure due to governance weaknesses",
        examples: ["Inadequate financial oversight", "Weak controls", "Poor reserves management"]
      },
      {
        type: "Strategic Risk",
        definition: "Risk of strategic failure due to governance deficiencies",
        examples: ["Inadequate board capability", "Poor strategic oversight", "Lack of performance monitoring"]
      }
    ],
    
    likelihoodScale: [
      { level: 5, label: "Almost Certain", definition: "Expected to occur in most circumstances" },
      { level: 4, label: "Likely", definition: "Will probably occur" },
      { level: 3, label: "Possible", definition: "Might occur at some time" },
      { level: 2, label: "Unlikely", definition: "Could occur but not expected" },
      { level: 1, label: "Rare", definition: "May occur only in exceptional circumstances" }
    ],
    
    impactScale: [
      { level: 5, label: "Critical", definition: "Catastrophic impact on organisation" },
      { level: 4, label: "Major", definition: "Significant impact requiring board intervention" },
      { level: 3, label: "Moderate", definition: "Noticeable impact, manageable at executive level" },
      { level: 2, label: "Minor", definition: "Limited impact, routine management" },
      { level: 1, label: "Negligible", definition: "Minimal impact" }
    ],
    
    prioritizationMatrix: {
      critical: "Likelihood × Impact ≥ 15 (Immediate action required)",
      high: "Likelihood × Impact 10-14 (Action required within 30 days)",
      medium: "Likelihood × Impact 6-9 (Action required within 90 days)",
      low: "Likelihood × Impact ≤ 5 (Monitor and address as resources permit)"
    }
  },

  recommendationFormulation: {
    principles: [
      "Evidence-based: Every recommendation must be supported by specific findings",
      "Actionable: Clear, specific actions that can be implemented",
      "Prioritized: Ranked by risk severity and implementation urgency",
      "Realistic: Achievable within organisational capacity and resources",
      "Measurable: Include success criteria where possible"
    ],
    
    recommendationStructure: {
      finding: "What was observed (evidence-based)",
      risk: "Why it matters (impact and risk)",
      recommendation: "What should be done (specific action)",
      priority: "When it should be done (timeframe)",
      responsibility: "Who should lead (role/function)"
    }
  },

  professionalStandards: {
    tone: "Authoritative, analytical, professional. Avoid generic corporate language or oversimplification.",
    language: "Use formal UK corporate governance terminology. Write at postgraduate professional level.",
    structure: "Logical flow with clear cross-referencing. Executive summary for board, detailed analysis for implementation.",
    regulatory: "Maintain regulatory awareness without giving legal advice. Reference Charity Governance Code, Charity Commission guidance.",
    quality: "This must read as a £10k+ consultancy deliverable, not a template or checklist."
  }
};
